import { useEffect, useMemo, useState } from "react";
import { AuditLogTable, AuditLogDetailModal } from "@/components/audit";
import { AuditActionCombobox } from "@/components/audit/AuditActionCombobox";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
// Export is switched off (2026-09-11); restore this import with the toolbar block below.
// import { PermissionGate } from "@/components/common/PermissionGate";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DateRangePicker,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";
// Export is switched off (2026-09-11); restore this import with the toolbar block below.
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from "@/components/ui/dropdown-menu";
import {
  // Download, — Export is switched off (2026-09-11); restore with the toolbar block below.
  FileText,
  Filter,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  useAuditLogs,
  useAuditLogFilters,
  // useExportAuditLogs, — Export is switched off (2026-09-11); restore with the toolbar block below.
} from "@/hooks/useAuditQueries";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { formatDate } from "@/lib/utils";
import { formatAuditActionFilterLabel } from "@/lib/auditActions";
import type { AuditLogListFilters, AuditLogListItem } from "@/types/admin";
import { useAuditLogFiltersStore } from "@/stores/auditLogFiltersStore";
import {
  AUDIT_LOG_COLUMNS,
  useAuditLogColumnsStore,
} from "@/stores/auditLogColumnsStore";
import { getPaginationItems } from "@/lib/pagination";

/** Sentinel for "no filter" — Radix Select rejects an empty string as an item value. */
const ALL = "all";

export function AuditLogsPage() {
  // Filters live in the shared table store, like every other admin list: they survive a trip to a
  // detail page and back, and the operator's page size is remembered across sessions.
  const { search, action, entityType, dateRange } = useAuditLogFiltersStore(
    (state) => state.filters,
  );
  const page = useAuditLogFiltersStore((state) => state.page);
  const pageSize = useAuditLogFiltersStore((state) => state.pageSize);
  const setFilter = useAuditLogFiltersStore((state) => state.setFilter);
  const setPage = useAuditLogFiltersStore((state) => state.setPage);
  const setPageSize = useAuditLogFiltersStore((state) => state.setPageSize);
  const resetStoreFilters = useAuditLogFiltersStore(
    (state) => state.resetFilters,
  );
  const hasActiveFilters = useAuditLogFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // Which columns are on screen. `hidden` rather than the store's `isVisible`, for the reason
  // AuditLogTable gives: it is the value that actually changes when a column is toggled.
  const hiddenColumns = useAuditLogColumnsStore((state) => state.hidden);
  const toggleColumn = useAuditLogColumnsStore((state) => state.toggle);
  const showAllColumns = useAuditLogColumnsStore((state) => state.showAll);

  // The input holds what is being typed; the store holds what the query runs with.
  const [searchInput, setSearchInput] = useState(search);
  const [selectedLog, setSelectedLog] = useState<AuditLogListItem | null>(null);

  // Debounced so filtering does not fire a request per keystroke. 750ms matches the other admin
  // list screens.
  useEffect(() => {
    if (searchInput === search) return;

    const timer = setTimeout(() => {
      setFilter("search", searchInput);
    }, 750);

    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  // The dropdowns are built from the rows that exist, not from a list declared in the frontend —
  // there is no backend enum behind `action`, so a hard-coded list drifts the moment one is added.
  // While this is loading or has failed, `facets` is undefined and both selects silently fall
  // back to offering only their "All ..." option — indistinguishable from a platform that has
  // genuinely logged nothing yet, so the loading and error states need their own words.
  const {
    data: facets,
    isLoading: isFacetsLoading,
    isError: isFacetsError,
    isFetching: isFacetsFetching,
    refetch: refetchFacets,
  } = useAuditLogFilters();

  const { fromDate, toDate } = useMemo(
    () =>
      dateRange.from && dateRange.to
        ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
        : { fromDate: undefined, toDate: undefined },
    [dateRange],
  );

  const filters: AuditLogListFilters = useMemo(
    () => ({
      search: search || undefined,
      action: action || undefined,
      entityType: entityType || undefined,
      fromDate,
      toDate,
      page,
      pageSize,
    }),
    [search, action, entityType, fromDate, toDate, page, pageSize],
  );

  const { data: response, isFetching, error, refetch } = useAuditLogs(filters);

  // A term typed but not yet committed: the 750ms debounce has not fired, so nothing is fetching
  // and the table still shows the previous result. Without a signal here the screen sits
  // motionless for three quarters of a second after every keystroke and reads as frozen.
  const isSearchPending = searchInput !== search;

  // Export is switched off (2026-09-11); restore this with the toolbar block below.
  // const exportLogs = useExportAuditLogs();

  const logs = response?.data ?? [];

  // Fetching with nothing on screen yet — the first load, or a filter change that emptied the
  // cache. That is the case skeleton rows are for; with rows in hand the pill above takes over.
  const isReloading = isFetching && logs.length === 0;
  const totalPages = response?.pagination?.totalPages ?? 1;
  const totalCount = response?.pagination?.totalItems ?? 0;

  // The store's page, not the response's: this query keeps placeholder data, so while a page
  // change is in flight `response` still describes the page being replaced - the label said the
  // old number, the row numbering restarted from it, and the highlighted page button lagged one
  // request behind the click.
  const currentPage = page;

  // The search box holds its own copy of what is being typed, so clearing the store alone would
  // leave the old term sitting in the input.
  const resetFilters = () => {
    setSearchInput("");
    resetStoreFilters();
  };

  // One button for both jobs, as on the other admin lists: clear the filters if there are any,
  // otherwise re-read the current view.
  const handleRefresh = () => {
    if (hasActiveFilters) {
      resetFilters();
    } else {
      void refetch();
    }
  };

  // Only take the screen over when there is genuinely nothing to show. A refetch that fails - a
  // token refresh losing a race, a blip on one filter change - used to replace the table AND the
  // whole filter bar with a Retry card, so the operator had to rebuild their filters to get back to
  // rows that were still on screen a moment earlier. With a response in hand the failure is
  // reported in a banner above the table instead, and Retry is one click away either way.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">Failed to load audit logs</p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the Card, not the viewport: `fixed inset-0` centred this on the screen, so
            on a short table it hovered in blank space well below the rows it described.

            `!isReloading` keeps the two loading signals mutually exclusive — skeleton rows while
            there is nothing to show, the pill only while refreshing rows the operator can still
            see. Both at once said "Updating..." over a table that had never shown anything. */}
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          {/* The shared admin toolbar: search (360px) · filter chips · Filters popover (icon) ·
              column settings. Every control but the search input is h-8, the same metrics as the
              tenant, resource and payment lists. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {/* The debounce window has no isFetching to report, so the box says so itself. */}
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  id="search-audit-logs"
                  type="text"
                  placeholder="Search ID, email, keyword"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  // React withholds onChange while an IME composition is open — which is the
                  // whole time a Vietnamese Telex/VNI method is assembling a word — so the term
                  // only reached state once a space closed the composition, and the search
                  // appeared to need a trailing space to run at all.
                  onCompositionEnd={(event) =>
                    setSearchInput(event.currentTarget.value)
                  }
                  className="h-9 pl-10 pr-9"
                />
              </div>
            </div>

            <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
              {search && (
                <FilterChip
                  label={`Search: ${search}`}
                  onClear={() => {
                    setSearchInput("");
                    setFilter("search", "");
                  }}
                />
              )}
              {entityType && (
                <FilterChip
                  label={`Entity: ${entityType}`}
                  onClear={() => setFilter("entityType", "")}
                />
              )}
              {action && (
                <FilterChip
                  label={`Action: ${formatAuditActionFilterLabel(action)}`}
                  onClear={() => setFilter("action", "")}
                />
              )}
              {dateRange.from && dateRange.to && (
                <FilterChip
                  label={`Date: ${formatDate(dateRange.from)} – ${formatDate(dateRange.to)}`}
                  onClear={() => setFilter("dateRange", {})}
                />
              )}
            </div>

            <Popover>
              {/* Icon only, so the accessible name has to come from somewhere: `title` gives the
                  hover tooltip, `aria-label` gives screen readers the same words. */}
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 space-y-4 p-4">
                {/* In the popover with the other secondary filters rather than on the toolbar, which
                    carries only the search box, the chips and the icon controls on every admin
                    list. */}
                <div className="space-y-2">
                  <Label htmlFor="auditEntityTypeFilter">Entity type</Label>
                  <Select
                    value={entityType || ALL}
                    onValueChange={(value) =>
                      setFilter("entityType", value === ALL ? "" : value)
                    }
                  >
                    <SelectTrigger id="auditEntityTypeFilter">
                      <SelectValue placeholder="All entity types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>All entity types</SelectItem>
                      {facets?.entityTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FacetsHint
                    isLoading={isFacetsLoading}
                    isError={isFacetsError}
                    isFetching={isFacetsFetching}
                    onRetry={refetchFacets}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="auditActionFilter">Action</Label>
                  {/* Searchable: the table holds dozens of distinct actions, and a plain Select
                      made the operator scroll the whole list for one. */}
                  <AuditActionCombobox
                    id="auditActionFilter"
                    value={action}
                    onChange={(value) => setFilter("action", value)}
                    actions={facets?.actions ?? []}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="auditDateFilter">Date range</Label>
                  <DateRangePicker
                    value={dateRange}
                    onChange={(range) => setFilter("dateRange", range)}
                    placeholder="Select date range"
                    className="w-full"
                  />
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={AUDIT_LOG_COLUMNS}
              isVisible={(id) => !hiddenColumns.includes(id)}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
            />

            {/* Export switched off on 2026-09-11 at the user's request. The endpoint, the
                useExportAuditLogs hook and the service method are all kept. To restore: uncomment
                the exportLogs hook call and the PermissionGate, DropdownMenu and Download imports
                above, then put this block back. It is SuperAdmin-gated because only SuperAdmin may
                call the export endpoint, and it exports the same filters as the list minus paging.

                <PermissionGate roles={["SuperAdmin"]}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        disabled={exportLogs.isPending}
                        className="h-8 min-h-8 gap-2 px-2 text-sm hover:translate-y-0 active:translate-y-0"
                      >
                        <Download className="h-4 w-4" />
                        {exportLogs.isPending ? "Exporting..." : "Export"}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportLogs.mutate({ format: "csv", filters })}>
                        Export as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportLogs.mutate({ format: "json", filters })}>
                        Export as JSON
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </PermissionGate>
            */}
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {/* Reached only with a previous result in hand - the no-data case returned above. A
              failed background refetch says so here and leaves the table and the filter bar
              alone. */}
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isReloading ? (
            /* Skeleton rows, not the empty state and not a bare table: "No audit logs yet" about a
               query still in flight is a lie, and a page that appears out of nothing reads as a
               stall followed by a jolt. min-h-0 flex-1 so the card keeps its height across all
               three branches. */
            <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <FileText className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No results found" : "No audit logs yet"}
              </h3>
              <p className="mb-6 text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Activity across the platform will appear here as it happens."}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={resetFilters}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <AuditLogTable
              logs={logs}
              startIndex={(currentPage - 1) * pageSize}
              onViewDetails={setSelectedLog}
            />
          )}

          {/* Outside the three branches above, so a search that matched nothing still shows its
              pager, its page-size picker and the reload button. It reads "Page 1 of 1 (0 total)"
              with both arrows disabled - a true statement about a result that came back, and the
              row an operator needs to change the page size or step back after a filter emptied the
              list. `!isReloading` is the one case held back: with no answer yet, "0 total" would
              describe a request still in flight. */}
          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value, 10))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                    single page made the controls appear and disappear as filters changed. Previous
                    and Next disable themselves at the ends. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Moved down from the toolbar to sit with the other paging controls, the same
                    place every other admin list keeps it. Icon only, and the tooltip says what the
                    click actually does: this button clears the filters when there are any and
                    only reloads when there are none, so a caption reading "Refresh" would promise
                    a reload while quietly emptying the filter bar. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedLog && (
        <AuditLogDetailModal
          log={selectedLog}
          open={selectedLog !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedLog(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Sits under the Entity type and Action selects. Both silently fall back to offering only their
 * "All ..." option while `useAuditLogFilters` is loading or has failed — indistinguishable from a
 * platform that has genuinely logged nothing under either filter, so the two states need their
 * own line rather than nothing at all.
 */
function FacetsHint({
  isLoading,
  isError,
  isFetching,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
}) {
  const placement = "mt-1";

  if (isLoading) {
    return (
      <p className={`${placement} flex items-center gap-1 text-xs text-muted-foreground`}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading filter options…
      </p>
    );
  }

  if (isError) {
    return (
      <p className={`${placement} flex items-center gap-1 text-xs text-destructive-ink`}>
        Could not load filter options.
        {/* `isError` stays true while a retry runs, so the pending state has to come from
            `isFetching` — otherwise the link looked dead and invited a second click. */}
        <button
          type="button"
          onClick={onRetry}
          disabled={isFetching}
          className="inline-flex cursor-pointer items-center gap-1 underline hover:no-underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-70"
        >
          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
          Retry
        </button>
      </p>
    );
  }

  return null;
}

/**
 * One active-filter chip. Same shape as the other admin list screens: shows what is applied and
 * clears just that one.
 */
function FilterChip({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <Badge
      variant="outline"
      className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label}`}
        className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}
